import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui';
import { findNode } from './dragRules';
import { UnitTypePicker } from './UnitTypePicker';
import type { HierarchyPageVM } from './useHierarchyPage';

export interface OrgRetypeDialogProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1838: "Đổi loại đơn vị" — change ONE đơn vị's kind.
 *
 * REPLACED `OrgTierAssignDialog`. Two behavioural differences, both consequences
 * of the model change rather than styling choices:
 *
 *   1. There is no "— Không có —" option. A đơn vị always has a type; the server
 *      rejects clearing it outright (`Unit type cannot be cleared.`), because a
 *      typeless row is what let the reparent rule skip itself.
 *   2. Changing the type can invalidate where the đơn vị ALREADY sits — its
 *      parent's type may not allow the new one. The server re-grades containment
 *      against the current parent and refuses in that case, so the warning below
 *      states it plainly instead of letting the admin discover it on their next
 *      drag.
 *
 * DELIBERATELY LIGHTWEIGHT, and it shares `UnitTypePicker` with the create modal
 * so the inline-create branch is available here too — the same "I need a kind
 * that does not exist yet" moment can happen while retyping. The picker itself
 * gates that branch on `canManageTree`; this dialog is reachable on `canManage`,
 * matching `OrganizationDetailView.patch`, whose super-admin gate covers
 * `is_active` and `parent` only.
 */
export function OrgRetypeDialog({ vm }: OrgRetypeDialogProps) {
    const { t } = useTranslation();
    const {
        tree,
        activeUnitTypes,
        unitTypesById,
        retypeNodeId,
        retypeChoice,
        setRetypeChoice,
        retypeError,
        closeRetype,
        confirmRetype,
        retypeOrg,
        unitTypesQuery,
        canManageTree,
        createInlineUnitType,
        createUnitType,
    } = vm;

    const node = retypeNodeId ? findNode(tree, retypeNodeId) : null;
    const currentTypeId = node?.organization.unit_type ?? null;
    const currentType = currentTypeId ? unitTypesById.get(currentTypeId) : undefined;

    // THE PICKER LISTS ACTIVE TYPES PLUS THE CURRENT ONE, even when that current
    // type has been hidden. Dropping a hidden-but-assigned type would make the
    // <select> silently render as something the đơn vị is NOT — the classic
    // controlled-select-with-a-missing-value bug — and would turn "open the
    // dialog, press Lưu" into an unintended retype.
    const options =
        currentType && !currentType.is_active
            ? [...activeUnitTypes, currentType]
            : activeUnitTypes;

    // The parent's type is what a newly created type should be allowed to sit
    // under, exactly as in the create modal.
    const parentUnitTypeId = node?.organization.parent
        ? (vm.knownOrgs.get(node.organization.parent)?.unit_type ?? null)
        : null;

    const unchanged = retypeChoice === currentTypeId;

    return (
        <Modal
            open={Boolean(node)}
            onCancel={closeRetype}
            title={
                node
                    ? t('console.hierarchy.retypeDialog.title', { name: node.organization.name })
                    : ''
            }
            footer={
                <div className="hz-move-footer">
                    <span className="hz-move-footer-note">
                        {t('console.hierarchy.retypeDialog.footerNote')}
                    </span>
                    <span className="hz-move-footer-actions">
                        <button
                            type="button"
                            className="portal-btn"
                            onClick={closeRetype}
                            disabled={retypeOrg.isPending}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="portal-btn portal-btn-primary"
                            onClick={confirmRetype}
                            disabled={retypeOrg.isPending || unchanged || !retypeChoice}
                        >
                            {retypeOrg.isPending
                                ? t('console.hierarchy.retypeDialog.savingLabel')
                                : t('common.save')}
                        </button>
                    </span>
                </div>
            }
        >
            <p className="hz-card-note">{t('console.hierarchy.retypeDialog.description')}</p>

            <UnitTypePicker
                options={options}
                value={retypeChoice}
                onChange={setRetypeChoice}
                canCreate={canManageTree}
                parentUnitTypeId={parentUnitTypeId}
                onCreate={createInlineUnitType}
                creating={createUnitType.isPending}
                disabled={retypeOrg.isPending}
            />

            <p className="hz-card-note">{t('console.hierarchy.retypeDialog.warning')}</p>

            {unitTypesQuery.isError && (
                <p className="portal-admin-error">
                    {t('console.hierarchy.retypeDialog.loadError')}
                </p>
            )}
            {retypeError && <p className="portal-admin-error">{retypeError}</p>}
        </Modal>
    );
}

export default OrgRetypeDialog;
